import { Flex, Form, Row } from '@/components';
import { KeywordColField } from '@/features/search/_components/_common/fields/KeywordColField';
import { NestedOrganizationIdsColField } from '@/features/search/_components/_common/fields/NestedOrganizationIdsColField';
import { ProjectNumbersColField } from '@/features/search/_components/_common/fields/ProjectNumbersColField';
import { useCreateQueryStore } from '@/features/search/_hooks/query-store';
import { useProjectsSearchStore } from '@/features/search/_stores/projects-search-store';
import { processAnalysisCascaderComparison } from '@/features/search/_utils/analysis-cascader-comparison';
import { validateProjectNumber } from '@/features/search/_utils/projects-search';
import { searchQueryKeyMap } from '@/features/search/_utils/query-keys';
import { useInvalidateQuery, useIsFetching } from '@/hooks';
import { isPS } from '@/utils';
import { type FC, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useFundSheetAnalysisPageSearchQueryStates } from '../_hooks/search-query-states';
import type { KsAnalysisSearchFormType } from '../_models/ks-analysis-search';
import { buildSubmitPageReset } from '../_utils/build-table-state-updates';
import { getKsOrganizationLeafIds } from '../_utils/get-ks-organization-leaf-ids';
import { SearchButton } from './SearchButton';
import { KsAggregationTargetColField } from './fields/KsAggregationTargetColField';
import { KsAnalysisAxisColField } from './fields/KsAnalysisAxisColField';
import { KsFiscalYearColField } from './fields/KsFiscalYearColField';
import { KsPaymentColField } from './fields/KsPaymentColField';
import { KsAggregationTags } from './tags/KsAggregationTags';
import { KsAnalysisSearchSearchFieldTags } from './tags/KsAnalysisSearchSearchFieldTags';

const FORM_ID = 'fund-sheet-analysis-search';

export const SearchForm: FC<{
  onResetPayment: () => void;
  resetCount: number;
}> = ({ onResetPayment, resetCount }) => {
  const { invalidate } = useInvalidateQuery();
  const { mutateAsync: createQueryStore } = useCreateQueryStore();
  const { setSearchQueryStates, searchQueryStates } =
    useFundSheetAnalysisPageSearchQueryStates();
  const isFetching = useIsFetching({
    queryKey: searchQueryKeyMap.ksAnalysis(),
  });

  const { cascaderTargetIds: currentCascaderValues } = useProjectsSearchStore();
  const {
    handleSubmit,
    formState: { isDirty },
    setError,
  } = useFormContext<KsAnalysisSearchFormType>();

  const onSubmit = useCallback(
    async (values: KsAnalysisSearchFormType) => {
      const { hasError: hasProjectNumberError } = validateProjectNumber(
        values,
        setError,
      );
      if (hasProjectNumberError) {
        return;
      }

      const { newCascadeId } = await processAnalysisCascaderComparison({
        values,
        currentCascaderValues,
        prevCascaderId: searchQueryStates.cascader,
        createQueryStore,
      });

      // 公開サイト(PS)には QueryStore API が無いため、事業所管課室は cascader ではなく
      // leaf id 配列(organizationIds)で保持する。PS では cascader を必ず null にして
      // 真実を organizationIds に寄せる（URL に古い cascader が残っていても API へ送らない）。
      // 内部サイトは従来どおり cascader を使い organizationIds は null（RS-4474）。
      const isPublicSite = isPS();

      // フォームの null（軸なし）は URL では `total` センチネルで明示する（合計のみ）。
      // URL から消すと初回表示フォールバック（organization）と区別できないため（RS-4424）。
      const nextAnalysisAxis = values.analysisAxis ?? 'total';

      setSearchQueryStates((prev) => ({
        ...prev,
        ...buildSubmitPageReset(),
        fiscalYear: values.fiscalYear?.year() ?? prev.fiscalYear,
        q: values.q ?? null,
        isSearchTargetProjectName: values.isSearchTargetProjectName ?? false,
        corporateNumber: values.corporateNumber ?? null,
        projectNumbers: values.projectNumbers ?? null,
        cascader: isPublicSite ? null : (newCascadeId ?? null),
        organizationIds: isPublicSite
          ? getKsOrganizationLeafIds(values.nestedOrganizationIds)
          : null,
        aggregationTargetType: values.aggregationTargetType,
        analysisAxis: nextAnalysisAxis,
        // 分析軸を変更して検索したときは、軸ごとに既定ソートが異なるため
        // 手動ソート(order)をリセットして各軸の初期ソートへ戻す (RS-4487)。
        ...(nextAnalysisAxis !== prev.analysisAxis ? { order: null } : {}),
      }));

      // Note: If any field's value change then based on query params it will call api anyway, So use isDirty to avoid invalidate call
      if (!isFetching && !isDirty) {
        invalidate(searchQueryKeyMap.ksAnalysis());
      }
    },
    [
      currentCascaderValues,
      invalidate,
      isFetching,
      isDirty,
      createQueryStore,
      setSearchQueryStates,
      searchQueryStates.cascader,
      setError,
    ],
  );

  return (
    <Form
      leavePageAlertCondition={null}
      id={FORM_ID}
      onFinish={handleSubmit(onSubmit)}
      layout="vertical"
    >
      <Flex vertical gap={16}>
        <Row gutter={16}>
          <KsFiscalYearColField />
        </Row>

        <Row gutter={16}>
          <KeywordColField />
          <NestedOrganizationIdsColField />
          <KsPaymentColField resetCount={resetCount} />
          <ProjectNumbersColField />
        </Row>

        <KsAnalysisSearchSearchFieldTags onResetPayment={onResetPayment} />

        <Row gutter={16}>
          <KsAggregationTargetColField />
          <KsAnalysisAxisColField />
        </Row>

        <KsAggregationTags />
        <SearchButton formId={FORM_ID} onResetPayment={onResetPayment} />
      </Flex>
    </Form>
  );
};
